import { supabase } from "../supabase/SupabaseClient";

/* Lo que le falta a la ficha de un chofer: la tarjeta, el tacógrafo, y —sobre
 * todo— si las dos altas se cruzaron.
 *
 * ── EL PROBLEMA QUE ESTO HACE VISIBLE ────────────────────────────────────────
 *
 * Igna: *«RRHH da de alta a X chofer pero no saben si tiene el registro al día;
 * Marcos no lo da de alta pero mantiene la documentación»*. Medido el
 * 27/08/2026 cruzando `nomina` con `chofer_documentos` por DNI:
 *
 *     160  cruzan bien
 *      10  están en la nómina de Erick y NO tienen ficha con Marcos
 *      60  figuran activos con Marcos y NO están en la nómina
 *
 * **Esos 70 no aparecen en ninguna pantalla del hub.** El que abre la ficha de
 * uno de ellos ve media ficha vacía, y un campo vacío se lee igual que un dato
 * que falta cargar: manda a completar algo que en realidad hay que conciliar.
 *
 * ── NO SE ARREGLA SOLO, Y NO SE INTENTA ──────────────────────────────────────
 *
 * La pantalla NO da de alta a nadie ni en un lado ni en el otro. Cuál de los
 * dos padrones tiene razón es una decisión del negocio —un fletero legítimo y
 * una baja que no llegó se ven exactamente igual desde acá— y adivinarla sería
 * escribir en la tabla de otro sector. Se muestra y decide una persona.
 *
 * ── EL PIN SE PIDE APARTE, Y NUNCA SIN QUE LO PIDAN ─────────────────────────
 *
 * `chofer_pin` tiene su propia RLS por `ver.pin_ypf`: es la credencial de la
 * tarjeta YPF. Acá se consulta SI EXISTE la fila —eso lo puede saber
 * cualquiera que vea la ficha— y el número se trae recién al apretar. Traerlo
 * con el resto lo dejaría en la memoria del navegador de todo el que abra una
 * ficha, sin que nadie lo haya pedido.
 */

/* `{dato, error}` en vez de tirar: la ficha tiene que abrir aunque una de sus
 * consultas falle, y «no se pudo mirar» tiene que poder decirse distinto de
 * «no hay». Mismo contrato que el resto de los servicios del hub. */
const limpio = (dni) => String(dni ?? "").replace(/\D/g, "").replace(/^0+/, "");

/* Los dos padrones, cruzados para ESTE DNI.
 *
 * Se piden las dos filas y no un `count`: para poder decir «está en la nómina
 * como Chofer Larga Distancia» hace falta el contenido, y son dos filas.
 */
export async function cruceDe(dni) {
  const d = limpio(dni);
  if (!d) return { dato: null, error: "" };

  try {
    const [nom, doc] = await Promise.all([
      supabase
        .from("nomina")
        .select("dni,apellido,nombres,legajo,categoria,calificacion,trabaja,es_chofer,empresa")
        .eq("dni", d)
        .limit(1),
      supabase
        .from("chofer_documentos")
        .select("dni,nombre,activo,empresa,zona")
        .eq("dni", d)
        .limit(1),
    ]);

    if (nom.error || doc.error) {
      return { dato: null, error: (nom.error || doc.error).message };
    }

    const enNomina = nom.data?.[0] || null;
    const enDocumentos = doc.data?.[0] || null;

    return {
      dato: {
        enNomina,
        enDocumentos,
        /* Los dos huecos, nombrados. `falta` vacío = las dos altas se
         * cruzaron, que es el caso de 160 de los 230. */
        falta: [
          ...(enNomina ? [] : ["nomina"]),
          ...(enDocumentos ? [] : ["documentos"]),
        ],
      },
      error: "",
    };
  } catch (e) {
    return { dato: null, error: e.message || "No se pudo consultar." };
  }
}

/* SI TIENE PIN, no cuál. Ver el encabezado. */
export async function tienePin(dni) {
  const d = limpio(dni);
  if (!d) return { dato: false, error: "" };
  try {
    const { count, error } = await supabase
      .from("chofer_pin")
      .select("dni", { count: "exact", head: true })
      .eq("dni", d);
    /* SIN `ver.pin_ypf` LA RLS DEVUELVE 0, NO UN ERROR, y esa es justamente la
     * razón por la que esto sólo puede contestar «sí» y nunca «no»: un cero
     * puede ser «no tiene» o «no podés verlo». Quien llama dibuja el aviso
     * únicamente cuando da true. */
    if (error) return { dato: false, error: error.message };
    return { dato: (count || 0) > 0, error: "" };
  } catch (e) {
    return { dato: false, error: e.message || "No se pudo consultar." };
  }
}

/* El número, recién cuando lo piden. */
export async function pinDe(dni) {
  const d = limpio(dni);
  if (!d) return { pin: "", error: "" };
  try {
    const { data, error } = await supabase
      .from("chofer_pin")
      .select("pin,actualizado_en")
      .eq("dni", d)
      .limit(1);
    if (error) return { pin: "", error: error.message };
    const f = data?.[0];
    if (!f) {
      // La RLS filtra sin avisar: sin el permiso esto llega vacío igual que si
      // no hubiera fila. Por eso se dice «no llegó» y no «no tiene»; qué
      // permiso falta no se nombra (Igna, 06/10: los mensajes no nombran llaves).
      return { pin: "", error: "No llegó el PIN." };
    }
    return { pin: String(f.pin || ""), cuando: f.actualizado_en, error: "" };
  } catch (e) {
    return { pin: "", error: e.message || "No se pudo consultar." };
  }
}

/* La tarjeta del tacógrafo.
 *
 * `dni` NULL con `revisado_en` puesto es «se miró y no se pudo vincular», que
 * no es lo mismo que una tarjeta sin revisar — lo dice el comentario de la
 * tabla. Acá se busca al revés (del DNI a la tarjeta), así que lo que puede
 * pasar es no encontrar nada, y eso significa que a esta persona todavía no se
 * le vinculó ninguna.
 */
export async function tacografoDe(dni) {
  const d = limpio(dni);
  if (!d) return { dato: null, error: "" };
  try {
    const { data, error } = await supabase
      .from("tacografo_chofer")
      .select("tarjeta,origen,revisado_en,confirmado_nombre,nota")
      .eq("dni", d)
      .limit(1);
    if (error) return { dato: null, error: error.message };
    return { dato: data?.[0] || null, error: "" };
  } catch (e) {
    return { dato: null, error: e.message || "No se pudo consultar." };
  }
}
